import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

/* ─── Keyframes ─────────────────────────────────────────────────────────── */

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0px rgba(62,143,147,0.7), 0 8px 32px rgba(12,30,40,0.3); }
  70%  { box-shadow: 0 0 0 16px rgba(62,143,147,0),   0 8px 32px rgba(12,30,40,0.3); }
  100% { box-shadow: 0 0 0 0px rgba(62,143,147,0),   0 8px 32px rgba(12,30,40,0.3); }
`;

const aiBounce = keyframes`
  0%   { transform: scale(1)    translateY(0); }
  20%  { transform: scale(1.08) translateY(-6px) rotate(-3deg); }
  40%  { transform: scale(0.95) translateY(2px)  rotate(2deg); }
  60%  { transform: scale(1.04) translateY(-3px) rotate(-1deg); }
  80%  { transform: scale(0.98) translateY(1px)  rotate(1deg); }
  100% { transform: scale(1)    translateY(0)    rotate(0); }
`;

const dotBlink = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; box-shadow: 0 0 8px rgba(67, 170, 139, 0.8); }
  100% { opacity: 0.4; }
`;

/* ─── Styled Components ─────────────────────────────────────────────────── */

const Wrapper = styled.div`
  position: fixed;
  bottom: 26px;
  right: 26px;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transform: ${({ $visible }) => $visible ? 'translateX(0)' : 'translateX(calc(100% - 32px))'};
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    transform: ${({ $visible }) => $visible ? 'translateX(0)' : 'translateX(calc(100% - 27px))'};
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 16px;
    transform: ${({ $visible }) => $visible ? 'scale(0.95) translateX(0)' : 'scale(0.95) translateX(calc(100% - 30px))'};
    transform-origin: bottom right;
  }
`;

const PillButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 8px;
  border-radius: 40px;
  border: 1.5px solid rgba(95, 190, 197, 0.45);
  background: linear-gradient(
    135deg,
    rgba(12, 30, 40, 0.92) 0%,
    rgba(22, 60, 70, 0.92) 100%
  );
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  cursor: pointer;
  outline: none;
  animation: ${pulseGlow} 2.5s ease-out infinite;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  /* Subtle top inner reflection for the glass effect */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 50%;
    background: linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%);
    border-radius: 40px 40px 0 0;
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(95, 190, 197, 0.9);
    background: linear-gradient(
      135deg,
      rgba(12, 30, 40, 0.98) 0%,
      rgba(45, 105, 115, 0.92) 100%
    );
    transform: translateY(-3px);
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  }

  &.bouncing {
    animation: ${aiBounce} 0.6s ease both;
  }

  @media (max-width: 480px) {
    padding: 6px 16px 6px 6px;
  }
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7CC9CC 0%, #3E8F93 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  position: relative;
  box-shadow: 0 4px 12px rgba(62, 143, 147, 0.4);
  z-index: 2;

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    margin-right: 10px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: 0px;
  right: -2px;
  width: 14px;
  height: 14px;
  background-color: #43AA8B;
  border: 2.5px solid #0C1E28;
  border-radius: 50%;
  z-index: 3;

  @media (max-width: 480px) {
    width: 12px;
    height: 12px;
    border-width: 2px;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  z-index: 2;
`;

const Title = styled.span`
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const Subtitle = styled.span`
  color: #99E1E4;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const BlinkDot = styled.span`
  width: 6px;
  height: 6px;
  background-color: #43AA8B;
  border-radius: 50%;
  display: inline-block;
  animation: ${dotBlink} 2s infinite ease-in-out;
`;

/* ─── Chat Icon ─────────────────────────────────────────────────────────── */

const ChatSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: 'translateY(-1px)' }}
  >
    {/* Clean, filled chat bubble */}
    <path
      d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H8L12 22L16 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
      fill="#ffffff"
    />
    {/* AI "typing" dots inside the bubble */}
    <circle cx="8"  cy="10" r="1.5" fill="#3E8F93" />
    <circle cx="12" cy="10" r="1.5" fill="#3E8F93" />
    <circle cx="16" cy="10" r="1.5" fill="#3E8F93" />
  </svg>
);

/* ─── Component ─────────────────────────────────────────────────────────── */

const GlobalChatWidget = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [bouncing, setBouncing] = useState(false);
  const [visible, setVisible]   = useState(false);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => setVisible(false), 3000);
  }, [clearHideTimer]);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(true);
      scheduleHide();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearHideTimer();
    };
  }, [scheduleHide, clearHideTimer]);

  const handleMouseEnter = useCallback(() => {
    clearHideTimer();
    setVisible(true);
  }, [clearHideTimer]);

  const handleMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);

  const handleClick = useCallback(() => {
    if (bouncing) return;
    setBouncing(true);
    // Pass the current pathname in router state so /chat knows where we came from
    setTimeout(() => navigate('/chat', { state: { from: location.pathname } }), 400);
    setTimeout(() => setBouncing(false), 700);
  }, [bouncing, navigate, location.pathname]);

  // Hide the widget when already on the chat page, support page, or admin pages
  if (
    location.pathname === '/chat' ||
    location.pathname === '/support' ||
    location.pathname.startsWith('/origin') ||
    location.pathname.startsWith('/user/dashboard/mock/exam-started') ||
    location.pathname.startsWith('/school/student')
  ) {
    return null;
  }

  return (
    <Wrapper
      $visible={visible}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <PillButton
        className={bouncing ? 'bouncing' : ''}
        onClick={handleClick}
        aria-label="Chat with AI Assistant"
      >
        <IconCircle>
          <ChatSVG />
          <OnlineIndicator />
        </IconCircle>
        
        <TextGroup>
          <Title>Chat with AI</Title>
          <Subtitle>
            <BlinkDot />
            Online now
          </Subtitle>
        </TextGroup>
      </PillButton>
    </Wrapper>
  );
};

export default GlobalChatWidget;
